import { type ReactNode } from 'react';

import { CheckboxGroup, ContentWithTooltip, Text } from '@ekhodealer/ekho-common/rev';

import Info16 from '@common/components/icons/Info16';

export interface FiltersDrawerCheckboxGroupItem {
	value: string;
	label: string | ReactNode;
	count: number;
}

interface FiltersDrawerCheckboxGroupProps {
	/** The label/title for the checkbox group */
	label?: string | ReactNode;
	/** Array of items to display as checkboxes */
	items: FiltersDrawerCheckboxGroupItem[];
	/** Controlled value - array of selected item values */
	value?: string[];
	/** Callback when selection changes */
	onChange?: (value: string[]) => void;
	/** Optional tooltip help text to display next to the label */
	tooltipHelpText?: string;
}

/**
 * FiltersDrawerCheckboxGroup component that provides a reusable checkbox group
 * with consistent styling for filter sections
 * Each checkbox displays its individual count from facets
 */
export function FiltersDrawerCheckboxGroup({
	label,
	items,
	value,
	onChange,
	tooltipHelpText,
}: FiltersDrawerCheckboxGroupProps) {
	const labelContent =
		label && typeof label === 'string' ? (
			<Text size='bodySmall' weight='semibold'>
				{label}
			</Text>
		) : (
			label
		);

	return (
		<CheckboxGroup
			label={
				tooltipHelpText ? (
					<div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
						{labelContent}
						<ContentWithTooltip
							content={
								<div style={{ display: 'flex', alignItems: 'center' }}>
									<Info16 />
								</div>
							}
							tooltipContent={tooltipHelpText}
						/>
					</div>
				) : (
					labelContent
				)
			}
			value={value}
			onChange={onChange}
			items={items.map((item) => ({
				value: item.value,
				label:
					typeof item.label === 'string' ? (
						<Text size='bodySmall'>{item.label}</Text>
					) : (
						item.label
					),
				itemProps: {
					rightContent: (
						<Text size='bodySmall' color='secondary'>
							{item.count}
						</Text>
					),
				},
			}))}
		/>
	);
}
